import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Play, Eye } from "lucide-react";
import { fmtCount } from "../../lib/text";
import { useSite } from "../../lib/site";
import { href } from "../../lib/i18n";
import { fmtDuration } from "../../lib/video";
import { Img } from "./Common";

function Slide({ v }) {
  const { lang, t } = useSite();
  return (
    <Link to={href(lang, "video", v.slug)} data-testid={`home-video-${v.slug}`} className="group block rounded-2xl border border-site-border bg-[#111319] overflow-hidden hover:border-[#d4af3780] transition-colors duration-500">
      <div className="relative aspect-video overflow-hidden bg-black">
        <Img src={v.thumbnail} alt={t(v.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#090a0dcc] via-transparent to-transparent" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-14 h-14 rounded-full bg-s-primary text-s-onprimary flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"><Play size={18} fill="currentColor" /></span>
        </span>
        {v.duration > 0 && <span className="absolute bottom-3 right-3 rounded bg-black/80 px-1.5 py-0.5 text-[11px] text-white">{fmtDuration(v.duration)}</span>}
      </div>
      <div className="p-5 space-y-2 min-h-[108px] flex flex-col">
        <p className="font-semibold text-white line-clamp-2 group-hover:text-s-primary transition-colors">{t(v.title)}</p>
        <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-auto" data-testid={`home-video-views-${v.slug}`}>
          <Eye size={13} />
          {(v.ext_views ?? v.views) > 0
            ? <>{fmtCount(v.ext_views ?? v.views, lang)} {lang === "tr" ? "izlenme" : "views"}</>
            : <span className="opacity-50">{lang === "tr" ? "izlenme güncelleniyor…" : "fetching…"}</span>}
        </p>
      </div>
    </Link>
  );
}

const BASIS = { 1: "basis-full", 2: "basis-full md:basis-1/2", 3: "basis-full md:basis-1/2 lg:basis-1/3", 4: "basis-full sm:basis-1/2 lg:basis-1/4" };

export function VideoSlider({ videos: all, config = {} }) {
  const per = [1, 2, 3, 4].includes(config.per_view) ? config.per_view : 3;
  const videos = all.slice(0, config.count || 12);
  const [ref, api] = useEmblaCarousel({ align: "start", loop: videos.length > per, slidesToScroll: "auto" });
  const [paused, setPaused] = useState(false);
  const secs = Math.min(30, Math.max(2, config.interval || 5));
  useEffect(() => {
    if (!api || !config.autoplay || paused || videos.length <= per) return;
    const id = setInterval(() => api.scrollNext(), secs * 1000);
    return () => clearInterval(id);
  }, [api, config.autoplay, paused, secs, videos.length, per]);
  const [snaps, setSnaps] = useState([]);
  const [cur, setCur] = useState(0);
  const sync = useCallback((a) => { setSnaps(a.scrollSnapList()); setCur(a.selectedScrollSnap()); }, []);
  useEffect(() => {
    if (!api) return;
    sync(api);
    api.on("select", sync).on("reInit", sync);
    return () => { api.off("select", sync).off("reInit", sync); };
  }, [api, sync]);
  const arrow = "w-11 h-11 rounded-full border border-site-border flex items-center justify-center text-white hover:bg-s-primary hover:text-s-onprimary hover:border-transparent transition-colors disabled:opacity-30";
  return (
    <div data-testid="home-video-slider" data-per-view={per} data-autoplay={config.autoplay ? "on" : "off"} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div className="overflow-hidden" ref={ref}>
        <div className="flex -ml-6">
          {videos.map((v) => <div key={v.id} className={`min-w-0 shrink-0 grow-0 pl-6 ${BASIS[per]}`}><Slide v={v} /></div>)}
        </div>
      </div>
      {snaps.length > 1 && (
        <div className="mt-8 flex items-center justify-between gap-4">
          <div className="flex gap-2" data-testid="home-video-dots">
            {snaps.map((_, i) => (
              <button key={i} type="button" aria-label={`${i + 1}`} onClick={() => api?.scrollTo(i)} data-testid={`home-video-dot-${i}`}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === cur ? "w-8 bg-s-primary" : "w-3 bg-slate-600 hover:bg-slate-400"}`} />
            ))}
          </div>
          <div className="flex gap-3">
            <button type="button" className={arrow} onClick={() => api?.scrollPrev()} aria-label="Önceki" data-testid="home-video-prev"><ChevronLeft size={18} /></button>
            <button type="button" className={arrow} onClick={() => api?.scrollNext()} aria-label="Sonraki" data-testid="home-video-next"><ChevronRight size={18} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
